import type { ClientActionDefinition } from './definition';
import { HOOK_CLIENT_ACTIONS } from './hooks/lexicon';
import { RAW_CLIENT_ACTIONS } from './redux/lexicon';

/**
 * The lexicon of client actions the AI planner can trigger, one list per kind:
 * 1. Redux actions, dispatched as is, with no per-action code. Prefer this kind for new actions.
 * 2. Hook actions (React Query), each needing its hook in the hook executor map.
 * The zod union, wire types, name unions, tool description and ACL lookup all derive from this
 * list. The server imports the lexicon files to build the tool's input schema, so their value
 * imports must stay zod, enums and constants. Creators are imported type-only.
 * Payload fields are never `.optional()` (OpenAI strict mode needs every key in `required`):
 * "not set" is an explicit `null` or empty string.
 */
export const CLIENT_ACTIONS = [...RAW_CLIENT_ACTIONS, ...HOOK_CLIENT_ACTIONS] as const;

// Structural check on the finished tuple — deliberately NOT an inline
// `satisfies` on the array literals: a contextual type there would widen the
// entries' literal keys to string and break the action union.
void (CLIENT_ACTIONS satisfies readonly ClientActionDefinition[]);

// Widened so the ACL can be compared against the whole union below.
const ALL_CLIENT_ACTIONS: readonly ClientActionDefinition[] = CLIENT_ACTIONS;

/**
 * Every action the browser may run. A disabled action is refused at execution,
 * whoever authored the batch.
 */
export const ENABLED_CLIENT_ACTIONS: readonly ClientActionDefinition[] = ALL_CLIENT_ACTIONS.filter(
  (definition) => definition.access !== 'disabled',
);

/**
 * What the model is actually offered. A server-authored action is neither
 * described to it nor accepted in its input, so a stale prompt that names one
 * fails at the edge instead of writing.
 */
export const MODEL_CLIENT_ACTIONS: readonly ClientActionDefinition[] =
  ENABLED_CLIENT_ACTIONS.filter((definition) => definition.authoredBy !== 'server');
